"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DashboardImpl = void 0;
const Contract = require("@tableau/api-external-contract-js");
const api_external_contract_js_1 = require("@tableau/api-external-contract-js");
const api_internal_contract_js_1 = require("@tableau/api-internal-contract-js");
const InternalToExternalEnumMappings_1 = require("../EnumMappings/InternalToExternalEnumMappings");
const Point_1 = require("../Point");
const ServiceRegistry_1 = require("../Services/ServiceRegistry");
const TableauError_1 = require("../TableauError");
const ErrorHelpers_1 = require("../Utils/ErrorHelpers");
const DashboardObjectImpl_1 = require("./DashboardObjectImpl");
const SheetImpl_1 = require("./SheetImpl");
const SheetInfoImpl_1 = require("./SheetInfoImpl");
const WorksheetImpl_1 = require("./WorksheetImpl");
class DashboardImpl extends SheetImpl_1.SheetImpl {
    constructor(_sheetInfo, _zones, _sheetPath, _registryId, _parentStoryPointImpl, _activeDashboardObjectId = 0) {
        super(_sheetInfo, _registryId);
        this._zones = _zones;
        this._sheetPath = _sheetPath;
        this._parentStoryPointImpl = _parentStoryPointImpl;
        this._activeDashboardObjectId = _activeDashboardObjectId;
    }
    get worksheetsImpl() {
        return this._worksheetsImpl;
    }
    get objects() {
        return this._objects;
    }
    get parentStoryPoint() {
        return this._parentStoryPointImpl;
    }
    get activeDashboardObjectId() {
        return this._activeDashboardObjectId;
    }
    get activeDashboardName() {
        return this._sheetPath.sheetName;
    }
    initializeWithPublicInterfaces() {
        this._worksheetsImpl = new Array();
        this._objects = new Array();
        this.zoneMap = new Map();
        // Process all the zones which are contained in this dashboard
        for (const zone of this._zones) {
            let worksheetImpl = undefined;
            const zoneSize = { width: zone.width, height: zone.height };
            // As the dashboard is active, all other zones in the dashboard are inactive.
            const isActive = false;
            if (zone.zoneType === api_internal_contract_js_1.DashboardObjectType.Worksheet || zone.zoneType === api_internal_contract_js_1.DashboardObjectType.QuickFilter) {
                let worksheetName = '';
                let worksheetUrl = '';
                let isHidden = false;
                if (zone.sheetInfo) {
                    // zone.sheetInfo was not initialized prior to internal-contract 1.6.0
                    worksheetName = zone.sheetInfo.name;
                    // worksheetUrl & isHidden is for Embedding only
                    worksheetUrl = zone.sheetInfo.url || '';
                    // If there's a url, then it's not hidden
                    isHidden = worksheetUrl === '';
                }
                else {
                    worksheetName = zone.name;
                }
                // Indexes, isActive and some more properties in sheetInfoImpl are embedding specific.
                // But we init them for both extensions and embedding as the Models will only use what is relevant.
                const sheetInfoImpl = new SheetInfoImpl_1.SheetInfoImpl(worksheetName, api_external_contract_js_1.SheetType.Worksheet, zoneSize, this._worksheetsImpl.length, isActive, isHidden, worksheetUrl);
                const vizId = {
                    worksheet: worksheetName,
                    dashboard: this._sheetInfoImpl.name,
                    storyboard: this._sheetPath.storyboard,
                    flipboardZoneID: this._sheetPath.flipboardZoneID,
                    storyPointID: this._sheetPath.storyPointID,
                };
                worksheetImpl = new WorksheetImpl_1.WorksheetImpl(sheetInfoImpl, this._registryId, vizId, this, this._parentStoryPointImpl);
                if (zone.zoneType === api_internal_contract_js_1.DashboardObjectType.Worksheet) {
                    this._worksheetsImpl.push(worksheetImpl);
                }
            }
            const zonePoint = new Point_1.Point(zone.x, zone.y);
            const dashboardObjectImpl = new DashboardObjectImpl_1.DashboardObjectImpl(this, InternalToExternalEnumMappings_1.InternalToExternalEnumMappings.dashboardObjectType.convert(zone.zoneType), zonePoint, zoneSize, worksheetImpl, zone.name, zone.isFloating !== undefined ? zone.isFloating : false, // before 1.6.0 we didn't have isFloating, so we assume false
            zone.isVisible !== undefined ? zone.isVisible : true, // before 1.6.0 we didn't have isVisible, so we assume true
            zone.zoneId, zone.fieldId);
            this._objects.push(dashboardObjectImpl);
            this.zoneMap.set(zone.zoneId, dashboardObjectImpl);
        }
    }
    setDashboardObjectVisibilityAsync(dashboardObjectVisibilityMap) {
        const zoneService = ServiceRegistry_1.ApiServiceRegistry.get(this._registryId).getService("zone-service" /* ServiceNames.Zone */);
        return zoneService.setVisibilityAsync(/*Dashboard Name*/ this.name, this.zoneMap, dashboardObjectVisibilityMap);
    }
    getDashboardObjectById(dashboardObjectId) {
        return this.zoneMap.get(dashboardObjectId);
    }
    updateZones(newZones, newActiveZoneId = 0, newActiveDashboardName = '') {
        // getting previous dashboard objects, active zone ID and active dashboard name
        const oldDashboardObjects = this._objects;
        const oldZoneMap = this.zoneMap;
        const oldActiveZoneId = this._activeDashboardObjectId;
        const oldActiveDashboardName = this._sheetPath.sheetName;
        // updating zones and reinitializing instance variables
        this._zones = newZones;
        this._activeDashboardObjectId = newActiveZoneId;
        if (newActiveDashboardName) {
            this._sheetPath.sheetName = newActiveDashboardName;
            this._sheetInfoImpl.name = newActiveDashboardName;
        }
        this.initializeWithPublicInterfaces();
        // getting new dashboard objects
        const newDashboardObjects = this._objects;
        const newZoneMap = this.zoneMap;
        // initializing map for changes
        const zoneChanges = new Map();
        // comparing old dashboard objects with new ones
        oldDashboardObjects.forEach((oldObject) => {
            const oldId = oldObject.id;
            // checking if zone was removed
            if (!newZoneMap.has(oldId)) {
                this.addChange(oldId, zoneChanges, api_external_contract_js_1.DashboardLayoutChange.Removed);
                return;
            }
            const newObject = newZoneMap.get(oldId);
            if (oldObject.isFloating !== newObject.isFloating) {
                this.addChange(oldId, zoneChanges, api_external_contract_js_1.DashboardLayoutChange.IsFloatingChanged);
            }
            if (oldObject.isVisible !== newObject.isVisible) {
                this.addChange(oldId, zoneChanges, api_external_contract_js_1.DashboardLayoutChange.IsVisibleChanged);
            }
            if (oldObject.name !== newObject.name) {
                this.addChange(oldId, zoneChanges, api_external_contract_js_1.DashboardLayoutChange.NameChanged);
            }
            if (oldObject.position.x !== newObject.position.x || oldObject.position.y !== newObject.position.y) {
                this.addChange(oldId, zoneChanges, api_external_contract_js_1.DashboardLayoutChange.PositionChanged);
            }
            if (oldObject.size.width !== newObject.size.width || oldObject.size.height !== newObject.size.height) {
                this.addChange(oldId, zoneChanges, api_external_contract_js_1.DashboardLayoutChange.SizeChanged);
            }
        });
        // Checking for any added zones
        newDashboardObjects.forEach((newObject) => {
            if (!oldZoneMap.has(newObject.id)) {
                this.addChange(newObject.id, zoneChanges, api_external_contract_js_1.DashboardLayoutChange.Added);
            }
        });
        // Checking if zone selection has changed
        if (oldActiveZoneId !== newActiveZoneId) {
            if (newActiveZoneId !== 0) {
                this.addChange(newActiveZoneId, zoneChanges, api_external_contract_js_1.DashboardLayoutChange.Selected);
            }
            if (oldActiveZoneId !== 0) {
                this.addChange(oldActiveZoneId, zoneChanges, api_external_contract_js_1.DashboardLayoutChange.Deselected);
            }
        }
        // Checking if switched to another dashboard
        if (newActiveDashboardName && oldActiveDashboardName !== newActiveDashboardName) {
            this.addChange(0, zoneChanges, api_external_contract_js_1.DashboardLayoutChange.DashboardChanged);
        }
        return zoneChanges;
    }
    addChange(zoneId, zoneChanges, change) {
        if (!zoneChanges.has(zoneId)) {
            zoneChanges.set(zoneId, []);
        }
        zoneChanges.get(zoneId).push(change);
    }
    moveAndResizeDashboardObjectsAsync(dashboardObjectPositionAndSizeUpdateArray) {
        const zoneService = ServiceRegistry_1.ApiServiceRegistry.get(this._registryId).getService("zone-service" /* ServiceNames.Zone */);
        return zoneService.moveAndResizeAsync(/*Dashboard Name*/ this.name, this.zoneMap, dashboardObjectPositionAndSizeUpdateArray);
    }
    replayAnimationAsync(replaySpeed) {
        const animationService = ServiceRegistry_1.ApiServiceRegistry.get(this._registryId).getService("animation-service" /* ServiceNames.Animation */);
        return animationService.replayAsync(replaySpeed);
    }
    getFiltersAsync() {
        this.verifyActiveSheetOrEmbeddedInActiveStoryPoint();
        const service = ServiceRegistry_1.ApiServiceRegistry.get(this._registryId).getService("filter-service" /* ServiceNames.Filter */);
        return service.getDashboardFiltersAsync();
    }
    applyFilterAsync(fieldName, values, updateType, options) {
        ErrorHelpers_1.ErrorHelpers.verifyEnumValue(updateType, Contract.FilterUpdateType, 'FilterUpdateType');
        ErrorHelpers_1.ErrorHelpers.verifyStringParameter(fieldName, 'fieldName');
        if (!Array.isArray(values)) {
            throw new TableauError_1.TableauError(api_external_contract_js_1.ErrorCodes.InvalidParameter, 'values parameter for applyDashboardFilterAsync must be an array');
        }
        this.verifyActiveSheetOrEmbeddedInActiveStoryPoint();
        const service = ServiceRegistry_1.ApiServiceRegistry.get(this._registryId).getService("filter-service" /* ServiceNames.Filter */);
        return service.applyDashboardFilterAsync(fieldName, values, updateType, options);
    }
    // @W-12986439: remove once initializeWithPublicInterfaces is moved to the constructor for this class
    // This method only exists since worksheetsImpl can be undefined, but we need the worksheet names in the Export APIs
    getWorksheetNamesFromZones() {
        const worksheetNames = [];
        for (const zone of this._zones) {
            if (zone.zoneType !== api_internal_contract_js_1.DashboardObjectType.Worksheet) {
                continue;
            }
            // zone.sheetInfo was not initialized prior to internal-contract 1.6.0
            const worksheetName = zone.sheetInfo ? zone.sheetInfo.name : zone.name;
            worksheetNames.push(worksheetName);
        }
        return worksheetNames;
    }
    verifyActiveSheetOrEmbeddedInActiveStoryPoint() {
        const isRootAndActiveDashboard = this.active;
        const isWithinActiveStoryPoint = this.parentStoryPoint != null && this.parentStoryPoint.active;
        if (!isRootAndActiveDashboard && !isWithinActiveStoryPoint) {
            throw new TableauError_1.TableauError(api_external_contract_js_1.SharedErrorCodes.NotActiveSheet, 'Operation not allowed on non-active sheet');
        }
    }
}
exports.DashboardImpl = DashboardImpl;
//# sourceMappingURL=DashboardImpl.js.map